'use client'

import type { ButtonHTMLAttributes } from 'react'

interface StepperButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  direction: 'increase' | 'decrease'
}

/**
 * The -/+ control used by every quantity stepper (cart modal length, pieces,
 * roll quantity, product page quantity).
 *
 * It draws its glyph as text rather than an icon on purpose. The icon set's
 * `minus` and `plus` are solar duotone *circle* glyphs, so inside a round
 * icon-only button they rendered as a circle within a circle, with the actual
 * stroke at half opacity: the product page's stepper read as two grey dots.
 *
 * Borders and glyph are foreground-weight, not muted, because these sit on
 * paper and white surfaces where a muted hairline all but disappears.
 */
export function StepperButton({ direction, className = '', ...props }: StepperButtonProps) {
  return (
    <button
      type="button"
      aria-label={direction === 'increase' ? 'Increase' : 'Decrease'}
      {...props}
      className={[
        'w-9 h-9 shrink-0 rounded-md bg-background border border-border text-foreground',
        'hover:border-foreground hover:bg-surface-secondary transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--focus)]',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        'flex items-center justify-center text-lg font-bold leading-none',
        className
      ].join(' ')}
    >
      {direction === 'increase' ? '+' : '−'}
    </button>
  )
}
